// utils/itemTagSettings.js
//
// ITEM TAGS, the client's pure half of the SETTINGS (docs/plans/tags.md §3.2,
// §5.2, ruled d29–d39, B19; keepr-api docs/SCHEMA.md § Item tags, § The impact
// report; build step T4d). No React, no network: what a card's "Use items as
// tags" setting is, the words each mode, pass and refusal is said in, and what
// an impact report entry is. utils/itemTags.js is its sibling for the tags ON
// an item (T4b); this file is about turning items INTO tags.
//
//   collections.cards[].itemTags   'off' | 'chosen' | 'every'
//   collections.cards[].applyWhereReferenced   default true (the where-referenced rule, T6b)
//
// The setting is read from the ITEM'S OWN collection's `cards[]`, and the
// NEAREST card in the card's parent chain whose entry carries one decides —
// so an entry on a card covers the cards that extend it unless one says
// otherwise (keepr-api utils/itemTags.js settingFor). Only
// `POST /api/collections/{id}/cards/{cardId}/item-tags` writes it: the
// collection PUT carries the stored values through and refuses a changed one
// (400 `item_tags_by_endpoint`), so a settings form never sends them.

import { formatCount, joinMeta } from './counts';
import { normalizeId } from './ids';
import { TAG_LIMITS, childrenOf, isRestrictedTag, positionAfter, tagIdOf, tagParentIdOf, treeOrder } from './tags';

export const ITEM_TAG_MODES = Object.freeze(['off', 'chosen', 'every']);

/** "1 item", "1,200 items", "0 items" — a count with its separators, zero included. */
const countOf = (count, one, many) => {
	const n = Math.max(0, Number(count) || 0);
	return `${n.toLocaleString('en-US')} ${n === 1 ? one : (many || `${one}s`)}`;
};
/** The keys of a `cards[]` entry only the item-tags endpoint writes. */
export const ITEM_TAG_KEYS = Object.freeze(['itemTags', 'applyWhereReferenced']);
/** Mirrored from keepr-api utils/itemTags.js LIMITS (ruled d35, d32). The API still judges. */
export const ITEM_TAG_SETTING_LIMITS = Object.freeze({ warnAt: 1000, refuseAbove: 10000, perParent: 200, names: 60 });

/** "Use items as tags: Off · Chosen items · Every item" — the three choices, in order (ruled d29). */
export const ITEM_TAG_MODE_LABELS = Object.freeze({
	off: 'Off',
	chosen: 'Chosen items',
	every: 'Every item',
});

/** One line saying what each mode means, for the person choosing it. */
export const ITEM_TAG_MODE_HINTS = Object.freeze({
	off: 'Items of this card are not used as tags here.',
	chosen: 'A manager turns an item’s tag on from the item: Use as a tag.',
	every: 'Every item of this card here has a tag, and each new one gets one.',
});

const MAX_CARD_WALK = 20;

export const isItemTagMode = value => ITEM_TAG_MODES.includes(value);
/** Does this mode make tags at all? */
export const itemTagsOn = mode => mode === 'chosen' || mode === 'every';

function refsByCard(collection) {
	const map = new Map();
	(Array.isArray(collection?.cards) ? collection.cards : []).forEach(ref => {
		const id = normalizeId(ref?.cardDefinitionId);
		if (id) map.set(id, ref);
	});
	return map;
}

/** A card entry's OWN mode, or null when it carries none. */
export const ownItemTagMode = ref => (isItemTagMode(ref?.itemTags) ? ref.itemTags : null);

/**
 * The setting that governs items of `cardId` in `collection`, read the way
 * the API reads it: the nearest card in its parent chain (itself first) whose
 * entry here carries a mode. `cardsById` gives each card's `parentCardId`.
 * → `{ mode, applyWhereReferenced, fromCardId }` — mode 'off' and fromCardId
 * null when nothing says otherwise.
 */
export function itemTagSettingFor(collection, cardId, cardsById = null) {
	const refs = refsByCard(collection);
	const none = { mode: 'off', applyWhereReferenced: true, fromCardId: null };
	let current = normalizeId(cardId);
	const seen = new Set();
	for (let depth = 0; current && depth < MAX_CARD_WALK; depth++) {
		if (seen.has(current)) break;
		seen.add(current);
		const ref = refs.get(current);
		const mode = ownItemTagMode(ref);
		if (mode) return { mode, applyWhereReferenced: ref.applyWhereReferenced !== false, fromCardId: current };
		const def = cardsById ? (cardsById instanceof Map ? cardsById.get(current) : cardsById[current]) : null;
		current = normalizeId(def?.parentCardId) || null;
	}
	return none;
}

/**
 * A card row's subtitle fragment in the card list: "Items used as tags
 * (Chosen items)", and — when the setting comes from a card it extends —
 * "(Every item, from Customer)". Null when the card's items are not tags.
 */
export function itemTagSettingPhrase(setting, { cardId = null, cardName } = {}) {
	if (!setting || !itemTagsOn(setting.mode)) return null;
	const label = ITEM_TAG_MODE_LABELS[setting.mode];
	const inherited = setting.fromCardId && normalizeId(setting.fromCardId) !== normalizeId(cardId);
	return inherited
		? `Items used as tags (${label}, from ${cardName || 'the card it extends'})`
		: `Items used as tags (${label})`;
}

/**
 * The settings form's `cards[]` with every item-tag key taken off: a
 * collection PUT that leaves them out carries the stored values through
 * (keepr-api collectionUtils.keepItemTagSettings), and one that sends a
 * value the endpoint has changed since is refused. ⚑ The form never sends
 * them at all (T4d): they are the endpoint's alone.
 */
export function withoutItemTagKeys(cards) {
	if (!Array.isArray(cards)) return cards;
	return cards.map(entry => {
		if (!entry || typeof entry !== 'object' || !ITEM_TAG_KEYS.some(key => key in entry)) return entry;
		const out = { ...entry };
		ITEM_TAG_KEYS.forEach(key => { delete out[key]; });
		return out;
	});
}

/**
 * `cards` (a form's merged list) with each entry's item-tag keys taken from
 * `stored` (the collection as it stands): the server's are the truth, and a
 * form that re-read the collection after the endpoint wrote must show what
 * the endpoint wrote even for an entry the person edited meanwhile.
 */
export function withStoredItemTagSettings(cards, stored) {
	if (!Array.isArray(cards)) return cards;
	const byId = refsByCard({ cards: stored });
	return cards.map(entry => {
		const id = normalizeId(entry?.cardDefinitionId);
		if (!id) return entry;
		const theirs = byId.get(id) || {};
		const out = { ...entry };
		ITEM_TAG_KEYS.forEach(key => {
			if (theirs[key] !== undefined) out[key] = theirs[key];
			else delete out[key];
		});
		return out;
	});
}

// ------------------------------------------------------------------ the passes

/**
 * A pass's state as a sentence (keepr-api utils/tagPasses.js toWire: `{ kind,
 * status, running, progress, finishedAt, error?, cancelledReason?, waiting? }`). Every
 * item's backfill (`itemTags.backfill`) counts `created`; a card's Off
 * (`itemTags.off`) counts `converted`, `deleted` and what it left on.
 * → `{ tone: 'info' | 'success' | 'warning', text, notes: [] }`, or null.
 */
export function passSummary(pass) {
	if (!pass || typeof pass !== 'object') return null;
	const progress = pass.progress && typeof pass.progress === 'object' ? pass.progress : {};
	const running = pass.running === true || pass.status === 'pending';
	const notes = [];
	// Since keepr-api T6b a backfill or an Off pass whose collection is
	// archived WAITS (`waiting: 'collection_archived'`), live, asked again
	// every ten minutes.
	const waitingWhy = passWaiting(pass) ? (MODE_WAITING[pass.waiting] || 'it cannot run yet. keepr tries again later.') : null;
	if (pass.kind === 'itemTags.backfill') {
		const made = Number(progress.created) || 0;
		if (waitingWhy) return { tone: 'warning', text: `Making tags is waiting: ${waitingWhy}`, notes };
		if (running) return { tone: 'info', text: made ? `Making tags: ${made} made so far.` : 'Making tags…', notes };
		if (pass.status === 'failed') {
			return { tone: 'warning', text: 'Making tags stopped before it finished. Choose Every item again to carry on where it stopped.', notes };
		}
		if (pass.status === 'cancelled' || progress.stale) {
			return { tone: 'info', text: joinMeta(made ? `${formatCount(made, 'tag')} made` : null, 'then the setting changed, so it stopped.'), notes };
		}
		return { tone: 'success', text: made ? `Every item has its tag: ${formatCount(made, 'tag')} made.` : 'Every item has its tag.', notes };
	}
	if (pass.kind === 'itemTags.off') {
		const kept = Number(progress.converted) || 0;
		const removed = Number(progress.deleted) || 0;
		const counts = joinMeta(
			kept ? `${formatCount(kept, 'tag')} kept as collection tags` : null,
			removed ? `${formatCount(removed, 'tag')} removed` : null
		);
		const privateSkipped = Number(progress.privateSkipped) || 0;
		const unconfirmed = Number(progress.unconfirmedSkipped) || 0;
		const deletedItems = Number(progress.deletedItemTags) || 0;
		if (privateSkipped) notes.push(`${formatCount(privateSkipped, 'tag was', 'tags were')} left on because the item was made private meanwhile. Turn each off from its item.`);
		if (unconfirmed) notes.push(`${formatCount(unconfirmed, 'tag was', 'tags were')} left on because the item was used as a tag after you checked the names. Turn each off from its item, or turn the card off again.`);
		if (deletedItems) notes.push(`${formatCount(deletedItems, 'deleted item', 'deleted items')} will come back without a tag if restored.`);
		if (waitingWhy) return { tone: 'warning', text: `Turning tags off is waiting: ${waitingWhy}`, notes };
		if (running) return { tone: 'info', text: counts ? `Turning tags off: ${counts} so far.` : 'Turning tags off…', notes };
		if (pass.status === 'failed') {
			return { tone: 'warning', text: 'Turning tags off stopped before it finished. Choose Off again to carry on where it stopped.', notes };
		}
		if (pass.status === 'cancelled' || progress.stale) {
			return { tone: 'info', text: joinMeta(counts || null, 'then the setting changed, so it stopped.'), notes };
		}
		return { tone: 'success', text: counts ? `Tags turned off: ${counts}.` : 'Tags turned off.', notes };
	}
	return null;
}

/** Is a pass still working (worth asking about again)? */
export const passRunning = pass => Boolean(pass) && (pass.running === true || pass.status === 'pending');

/** Is a live pass waiting for something before it can run (`waiting` set)? */
export const passWaiting = pass => passRunning(pass) && typeof pass.waiting === 'string' && pass.waiting !== '';

// Why a card's backfill or Off pass cannot run yet (`pass.waiting`, T6b).
const MODE_WAITING = {
	collection_archived: 'the collection is archived. keepr tries again every ten minutes.',
};

// Why a where-referenced pass cannot run yet (`refsPass.waiting`, keepr-api
// T6b) and when keepr looks again: a minute for the backfill, ten for an
// archived collection (itemTagRefs OPS.backfillWaitMs, tagPasses OPS.waitMs).
const REFS_WAITING = {
	item_tags_backfill: 'it waits for every item to get its tag first. keepr looks again every minute.',
	collection_archived: 'the collection, or a sub-collection holding items with these tags, is archived. keepr tries again every ten minutes.',
};

/**
 * The where-referenced pass (keepr-api T6b, `refsPass` on the item-tags GET
 * and on the switch's answer — kind `itemTags.refs`) as a sentence. It
 * applies the card's tags to the items that refer to their items (the switch
 * on; the Chosen items / Every item setting starts it too) or, switched off,
 * takes those applications off. Which one is the pass's own `mode` — 'apply'
 * or 'pull' (keepr-api, the tags rulings) — never the switch, which may have
 * moved since the pass began; `applying` (the switch as it stands) is the
 * fallback only for a pass that carries no mode (an older API). `progress`
 * counts `scanned` of `total` items (a share once the total is known), and
 * `applied` / `removed` applications.
 * → `{ tone, text, notes: [], waiting }`, or null.
 */
export function refsPassSummary(pass, { applying: switchOn = true } = {}) {
	if (!pass || typeof pass !== 'object') return null;
	const progress = pass.progress && typeof pass.progress === 'object' ? pass.progress : {};
	const n = key => Math.max(0, Number(progress[key]) || 0);
	const running = passRunning(pass);
	const notes = [];
	const applying = pass.mode === 'apply' || pass.mode === 'pull' ? pass.mode === 'apply' : switchOn;
	const doing = applying ? 'Applying the tags where their items are referenced' : 'Taking off the tags applied where their items are referenced';
	const overflow = n('overflowItems');
	const skipped = n('archivedSkipped');
	if (overflow) {
		notes.push(`${countOf(overflow, 'item refers', 'items refer')} to more than 500 items used as tags; ${overflow === 1 ? 'it keeps' : 'each keeps'} the first 500.`);
	}
	if (skipped) {
		notes.push(`${countOf(skipped, 'item was', 'items were')} left as ${skipped === 1 ? 'it was' : 'they were'}, because ${skipped === 1 ? 'its' : 'their'} sub-collection is archived.`);
	}
	if (passWaiting(pass)) {
		const why = REFS_WAITING[pass.waiting] || 'it cannot run yet. keepr tries again later.';
		return { tone: 'warning', text: `${doing} is waiting: ${why}`, notes, waiting: pass.waiting };
	}
	const changes = [
		n('applied') ? `${countOf(n('applied'), 'tag')} put on` : null,
		n('removed') ? `${countOf(n('removed'), 'tag')} taken off` : null,
	].filter(Boolean).join(', ');
	if (running) {
		const total = Number(progress.total);
		const checked = Number.isFinite(total) && total > 0
			? `${n('scanned').toLocaleString('en-US')} of ${countOf(total, 'item')} checked`
			: `${countOf(n('scanned'), 'item')} checked so far`;
		return { tone: 'info', text: `${doing}: ${checked}${changes ? `; ${changes}` : ''}.`, notes, waiting: null };
	}
	if (pass.status === 'failed') {
		return { tone: 'warning', text: `${doing} stopped before it finished. keepr tries again on its own.`, notes, waiting: null };
	}
	if (pass.status === 'cancelled' || progress.stale) {
		return { tone: 'info', text: `${doing} stopped before the end${changes ? ` (${changes})` : ''}: the setting changed.`, notes, waiting: null };
	}
	const lead = applying ? 'Applied where referenced' : 'Taken off where referenced';
	const checked = n('scanned') ? `${countOf(n('scanned'), 'item')} checked` : null;
	if (!changes) {
		return { tone: 'success', text: `${lead}: ${checked ? `${checked}, and nothing` : 'nothing'} needed to change.`, notes, waiting: null };
	}
	return { tone: 'success', text: `${lead}: ${checked ? `${checked}; ` : ''}${changes}.`, notes, waiting: null };
}

// ------------------------------------------------------------------ turning off

/** A conversion's name as the API would start it from an item's title (keepr-api itemTags.js defaultName). */
export function defaultTagNameFor(title) {
	const chars = [...`${title ?? ''}`.replace(/\s+/gu, ' ').trim()]
		.filter(ch => !/[\u0000-\u001F\u007F-\u009F]/.test(ch)) // eslint-disable-line no-control-regex
		.map(ch => (ch.normalize('NFKC').includes('/') ? '-' : ch));
	const name = chars.slice(0, ITEM_TAG_SETTING_LIMITS.names).join('').trim();
	return name || 'Untitled';
}

/**
 * What the off dialog's two choices amount to (ruled d30): keeping either
 * CONVERTS the tag into a collection tag; removing both DELETES it, and a
 * deletion is preceded by the impact report (d33). → 'convert' | 'delete' |
 * null (not both chosen yet).
 */
export function offOutcome({ automatic, hand } = {}) {
	if (!['remove', 'keep'].includes(automatic) || !['remove', 'keep'].includes(hand)) return null;
	return automatic === 'keep' || hand === 'keep' ? 'convert' : 'delete';
}

// ------------------------------------------------------------------ the impact report

/**
 * An impact report entry (keepr-api utils/tagReferences.js):
 *   { holder, id, collectionId, cardId?, name, paths, mine, scope?, cleanup, link, blocks?, blocksDelete? }
 * `cleanup` is what "Remove for me" does — 'delete' | 'removeBlock' | 'pause'
 * — or null (nothing can be removed from here: the link opens it).
 * `blocksDelete` (a tag's own report): a strict lookup's filter, which
 * refuses the tag's delete while it names the tag (b80).
 */
const HOLDER_LABELS = {
	pin: 'Pin on Home',
	savedFilter: 'Filter',
	cardLayout: 'Page layout',
	automation: 'Rule',
	notification: 'Notification',
	exportDefinition: 'Saved export',
	worklistFilter: 'Card worklist',
	drivenFilter: 'Element filled automatically',
	lookupFilter: 'Lookup filter',
	elementSetLookupFilter: 'Lookup filter in an element set',
	multiAddSeed: 'Multi add form',
};

/** The kind of thing an entry is, in the interface's words. */
export const referenceHolderLabel = entry => HOLDER_LABELS[entry?.holder] || 'Setting';

/** An entry's name, or what stands in for one. */
export function referenceName(entry) {
	const name = typeof entry?.name === 'string' ? entry.name.trim() : '';
	if (name) return name;
	if (entry?.holder === 'pin') return 'Your pin';
	return `Untitled ${referenceHolderLabel(entry).toLowerCase()}`;
}

/** What "Remove for me" does to this entry, in a few words; null when it offers none. */
export function referenceCleanupWords(entry) {
	switch (entry?.cleanup) {
		case 'delete':
			if (entry.holder === 'pin') return 'Unpins it.';
			if (entry.holder === 'cardLayout') return 'Deletes the page layout. History can bring it back.';
			return 'Deletes the filter whole. History can bring it back.';
		case 'removeBlock':
			return 'Removes the block that names the tag from the page layout.';
		case 'pause':
			return 'Pauses it. Nothing in it is changed.';
		default:
			return null;
	}
}

/** Can "Remove for me" be chosen for this entry? */
export const referenceOffersCleanup = entry => ['delete', 'removeBlock', 'pause'].includes(entry?.cleanup);

const referenceKey = entry => `${entry?.holder}:${entry?.id}`;
export { referenceKey };

/**
 * The `cleanup` a request carries: `[{ holder, id }]` for every entry the
 * person set to "Remove for me" (choices: key → 'remove' | 'leave'). Only
 * entries that offer one; never more than the API takes (200).
 */
export function cleanupFor(references = [], choices = {}) {
	return (Array.isArray(references) ? references : [])
		.filter(entry => referenceOffersCleanup(entry) && choices[referenceKey(entry)] === 'remove')
		.slice(0, 200)
		.map(entry => ({ holder: entry.holder, id: normalizeId(entry.id) || entry.id }));
}

/**
 * What the cleanup did, one sentence per kind of outcome (`[{ holder, id,
 * outcome: removed | paused | unchanged | not_found | refused, code? }]`).
 * → `{ done: 'Removed 2 and paused 1 for you.' | null, failed: [entry…] }`.
 */
export function cleanupSummary(outcomes = []) {
	const list = Array.isArray(outcomes) ? outcomes : [];
	const removed = list.filter(o => o?.outcome === 'removed').length;
	const paused = list.filter(o => o?.outcome === 'paused').length;
	const failed = list.filter(o => o?.outcome === 'refused');
	const parts = [];
	if (removed) parts.push(`removed ${removed}`);
	if (paused) parts.push(`paused ${paused}`);
	const done = parts.length ? `${parts.join(' and ').replace(/^./, ch => ch.toUpperCase())} for you.` : null;
	return { done, failed };
}

// ------------------------------------------------------------------ refusals

/**
 * Every code the item-tag routes answer (keepr-api § Item tags, § The impact
 * report), as the sentence a person reads and what to do about it. The API's
 * own sentences are plain too; ours win where a count or a next step reads
 * better in the interface's words.
 */
const REFUSALS = {
	invalid_mode: 'That setting could not be read. Choose Off, Chosen items or Every item.',
	invalid_choice: 'Choose Remove or Keep for both kinds of application.',
	invalid_cleanup: 'The choices for what refers to the tag could not be read. Open this again and choose once more.',
	invalid_id: 'That could not be found.',
	invalid_parent: 'The tag cannot go under that tag.',
	card_not_member: 'This card is not part of the collection yet. Save the collection first, then choose how its items are used as tags.',
	manage_required: 'Only managers of this collection can use its items as tags.',
	session_required: 'Removing what refers to a tag needs keepr itself, not a key.',
	collection_archived: 'This collection is archived, so its tags cannot change. Unarchive it first.',
	no_item_tag: 'This item is not used as a tag any more.',
	item_tags_off: 'Items of this card are not used as tags here. A manager can turn on Use items as tags for the card on the collection’s Cards tab.',
	item_pending_review: 'This item is awaiting review. Accept it before using it as a tag.',
	parent_full: `That tag already holds ${ITEM_TAG_SETTING_LIMITS.perParent} item tags, the most it can.`,
	confirm_names_required: 'The names these tags would get have changed since you looked. Check them again before keeping them.',
	stale_collection: 'The collection’s settings kept changing while this was saved. Try again.',
	modified_since: 'This item tag changed while it was being turned off. Try again.',
	tag_name_taken: 'A tag beside it already has that name. Choose another name.',
	legacy_tags_present: 'Tags aren’t available right now. Try again in a little while.',
	history_unavailable: 'The change history could not be written, so nothing changed. Try again in a moment.',
};

/**
 * A refusal from the item-tag routes as one plain sentence. `count`-bearing
 * refusals (Every item over the limits, the collection's room)
 * are worded here with their numbers.
 */
export function itemTagRefusal(error, fallback = 'That did not work. Please try again.') {
	if (!error?.response) return "We couldn't reach the server. Check your connection and try again.";
	const { status } = error.response;
	const data = error.response.data || {};
	const code = data.code || null;
	if (status === 429) {
		// The person's twenty writes an hour (`itemTagMode`, dry runs free since
		// keepr-api T6b), or the door of 120 calls. Since the tags rulings every
		// route limiter's 429 names its budget (`limit`, `window` in seconds);
		// ⚑ the sentence names it when it is there. A D34 switch budget
		// (`budget: 'switch'`) is its own switch's words (whereReferencedRefusal,
		// tagRuleRefusal).
		const limit = Number(data.limit);
		if (limit > 0 && data.budget !== 'switch') {
			return `Item tags can be changed ${limit.toLocaleString('en-US')} times ${perWindowWords(data.window)}, and that many changes have been made. ${retryAfterWords(data.retryAfter)}`;
		}
		return `Item tags have been changed many times in the last hour. ${retryAfterWords(data.retryAfter)}`;
	}
	switch (code) {
		case 'too_many_items':
			return `This card has ${Number(data.count) || 'more than 10,000'} items here. Every item works for up to ${ITEM_TAG_SETTING_LIMITS.refuseAbove.toLocaleString('en-US')} items, so use Chosen items instead.`;
		case 'confirm_required':
			return `This card now has ${Number(data.count) || 'more than 1,000'} items here, and each will get a tag. Look at the count again before going ahead.`;
		case 'private_item_tags': {
			const n = Number(data.count) || 0;
			const which = n === 1 ? 'One of these items is' : `${n || 'Some'} of these items are`;
			return `${which} not yours to read, and keeping would name a collection tag after it for everyone to see. Turn those off one by one from each item first, or remove both kinds of application.`;
		}
		case 'tag_limit_reached': {
			if (data.room !== undefined) {
				return `Keeping them would make ${formatCount(Number(data.count) || 0, 'collection tag') || 'more collection tags'}, and this collection has room for ${Number(data.room) || 0} more. Remove both kinds of application instead, or delete some tags first.`;
			}
			return 'This collection already has 500 tags, the most it can hold. Delete some first, or remove both kinds of application.';
		}
		case 'too_many_tags': {
			const n = Array.isArray(data.items) ? data.items.length : 0;
			return `${n === 1 ? 'An item already carries' : 'Some items already carry'} 50 tags put on by hand, so the rule’s application cannot become one there. Take a tag off ${n === 1 ? 'it' : 'them'} first, or remove the applications by a rule.`;
		}
		case 'invalid_tag':
			return typeof data.message === 'string' && data.message ? data.message : 'That name, icon or color cannot be used for a tag.';
		case 'confirm_names_required':
			// One item's keep (and a card with one item tag) confirms one name.
			return Number(data.count) === 1
				? 'The name this tag would get has changed since you looked. Check it again before keeping it.'
				: REFUSALS.confirm_names_required;
		default:
			break;
	}
	if (code && REFUSALS[code]) return REFUSALS[code];
	if (status === 404) return 'This is not there any more, or you can no longer see it.';
	if (status === 403) return 'Only managers of this collection can do this.';
	return (typeof data.message === 'string' && data.message) || fallback;
}

/**
 * A rate limit's `window` (seconds) as "an hour", "a minute", "a day", "every
 * 15 minutes"; "an hour" when it is missing (the item-tag budgets are hourly).
 */
export function perWindowWords(seconds) {
	const n = Math.round(Number(seconds) || 0);
	if (n <= 0 || n === 3600) return 'an hour';
	if (n === 60) return 'a minute';
	if (n === 86400) return 'a day';
	if (n % 3600 === 0) return `every ${n / 3600} hours`;
	if (n % 60 === 0) return `every ${n / 60} minutes`;
	return `every ${n} seconds`;
}

/** "Try again in 12 minutes." — a 429's `retryAfter` (seconds) as a sentence. */
export function retryAfterWords(seconds) {
	const n = Math.ceil(Number(seconds) || 0);
	if (n <= 0) return 'Try again in a little while.';
	if (n < 60) return `Try again in ${n} second${n === 1 ? '' : 's'}.`;
	const minutes = Math.ceil(n / 60);
	return `Try again in ${minutes} minute${minutes === 1 ? '' : 's'}.`;
}

/**
 * A refusal of Apply where referenced ON ITS OWN (keepr-api T6b: the POST
 * with `applyWhereReferenced` and no `mode`) as one plain sentence — its own
 * codes first, then everything the item-tag routes answer.
 *
 * - 429 `rate_limited` with `budget: 'switch'`: the card's six switches an
 *   hour (ruled D34; `limit`, `window`) — "switched 6 times this hour", and
 *   when to try again. Any other 429 is the person's own budget (every route
 *   limiter's 429 carries `limit` too since the tags rulings, so `limit`
 *   alone no longer tells them apart) — itemTagRefusal's words.
 * - 409 `item_tags_off`: the card's own entry is inherited or Off, so the
 *   switch belongs to the card the setting comes from.
 * - 400 `invalid_mode`: the call carried more than the switch.
 */
export function whereReferencedRefusal(error, { cardName = null } = {}) {
	if (!error?.response) return itemTagRefusal(error);
	const { status } = error.response;
	const data = error.response.data || {};
	if (status === 429 && data.budget === 'switch') {
		return `Apply where referenced has been switched ${Number(data.limit) || 6} times this hour${cardName ? ` for ${cardName}` : ''}, the most it can be. ${retryAfterWords(data.retryAfter)}`;
	}
	if (data.code === 'item_tags_off') {
		return `${cardName || 'This card'} no longer has a setting of its own that uses its items as tags here, so Apply where referenced cannot be switched on it. It is switched on the card the setting comes from.`;
	}
	if (data.code === 'invalid_mode') {
		return 'Apply where referenced could not be changed: the request asked for more than that. Close this window and try again.';
	}
	return itemTagRefusal(error, 'Apply where referenced could not be changed. Please try again.');
}

/**
 * Turning Apply where referenced off, as the confirmation says it: the dry
 * run's `automatic` (the items carrying one of the card's tags because they
 * refer to its item) come off; hand applications stay.
 */
export function whereReferencedOffWords(automatic) {
	const n = Math.max(0, Number(automatic) || 0);
	return `Takes the tag off ${countOf(n, 'item')} where it was applied because ${n === 1 ? 'it refers' : 'they refer'} to the item. Tags put on by hand stay, and turning this on again puts the others back.`;
}

/**
 * Why this person cannot turn item tags on or off here — the item-tag GET's
 * `canManage` is false (keepr-api: manage on the collection, and the
 * collection not archived) — as one plain sentence. `archived` is what the
 * surface knows of the collection; anything else is the manager rule.
 */
export function itemTagLockedWords({ archived = false } = {}) {
	return archived ? REFUSALS.collection_archived : REFUSALS.manage_required;
}

// ------------------------------------------------------------------ placing an item tag

/**
 * Move to… for an ITEM tag (ruled d28, b58): where it may sit under its
 * item's collection's own tags. An item tag is always the LAST of the three
 * levels, so a parent sits at level 1 or 2; it is a live kind-'tag' tag of the
 * item's collection (the vocabulary `tags` handed in — that collection's own,
 * never a parent collection's), never a restricted one (a restricted tag
 * stands alone). → `[{ id, tag, depth, disabled, reason, current }]` — the
 * top level first, then each tag in tree order, a place it can't go listed
 * disabled with why (TagMoveModal's list). A parent holds at most 200 item
 * tags: the API says so (409 `parent_full`), since the vocabulary never
 * lists item tags to count.
 */
export function itemTagPlaceTargets(tags = [], itemTag = null) {
	const current = tagParentIdOf(itemTag);
	const out = [{ id: null, tag: null, depth: 0, disabled: current === null, reason: current === null ? 'It is there now.' : null, current: current === null }];
	treeOrder((tags || []).filter(tag => tag && tag.kind !== 'item')).forEach(candidate => {
		const id = tagIdOf(candidate);
		const here = current === id;
		let reason = null;
		if (here) reason = 'It is there now.';
		else if (isRestrictedTag(candidate)) reason = 'A restricted tag stands alone: no tag can sit under it.';
		else if ((candidate.depth || 1) >= TAG_LIMITS.depth) reason = `Tags nest ${TAG_LIMITS.depth} levels deep at most, and an item tag is always the last.`;
		out.push({ id, tag: candidate, depth: candidate.depth || 1, disabled: Boolean(reason), reason, current: here });
	});
	return out;
}

/**
 * The body of `PUT /api/items/{itemId}/own-tag` that puts an item tag under
 * `parentId` (null: the top level): `{ parentId, position }`, the position
 * after the tags already there (⚑ the vocabulary lists no item tags, so it
 * goes after that parent's own tags).
 */
export function itemTagPlacement(tags = [], parentId = null) {
	const parent = normalizeId(parentId) || null;
	const siblings = childrenOf((tags || []).filter(tag => tag && tag.kind !== 'item'), parent);
	return { parentId: parent, position: positionAfter(siblings) };
}

/**
 * A refused Move to… of an item tag (`PUT …/own-tag { parentId, position }`)
 * as one sentence. `invalid_parent` says the API's own reason (another
 * collection's tag, a restricted one, too deep); `parent_full` its limit;
 * the rest as every item-tag route says them.
 */
export function itemTagMoveRefusal(error) {
	const data = error?.response?.data || {};
	if (data.code === 'invalid_parent') {
		return (typeof data.message === 'string' && data.message) || REFUSALS.invalid_parent;
	}
	if (data.code === 'parent_full') {
		const limit = Number(data.limit) || ITEM_TAG_SETTING_LIMITS.perParent;
		return `That tag already holds ${limit} item tags, the most it can. Choose another place, or move one of them out first.`;
	}
	return itemTagRefusal(error, 'The item tag could not be moved. Please try again.');
}

// ------------------------------------------------------------------ moves

/**
 * B20 (the T2c security review's X12): moving an item that is used as a tag,
 * when the move would take its tag off items outside its new family, needs
 * manage on the item's collection — else that record fails
 * `item_tag_carried` (`{ itemId, tagId, items, hand, automatic }`) and a move
 * is all or nothing. One record's reason, as the item move window lists it.
 */
export function itemTagCarriedWords(failure = {}) {
	const n = Number(failure.items) || 0;
	const where = n ? `on ${formatCount(n, 'other item')}` : 'on other items';
	return `it is used as a tag ${where} outside where it would go, and moving it would take the tag off ${n === 1 ? 'that item' : 'them'}. Only a manager of its collection can do that`;
}

/** A whole move refused for `item_tag_carried` (the card move), as one sentence. */
export function itemTagCarriedRefusal(failures = [], { target = null } = {}) {
	const list = Array.isArray(failures) ? failures.filter(f => f?.code === 'item_tag_carried') : [];
	const n = list.length || 1;
	const outside = target ? `outside ${target}` : 'outside where they would go';
	return `${n === 1 ? 'One of the items' : `${n} of the items`} ${n === 1 ? 'is' : 'are'} used as ${n === 1 ? 'a tag' : 'tags'} on items ${outside}. Moving ${n === 1 ? 'it' : 'them'} would take ${n === 1 ? 'that tag' : 'those tags'} off, and only a manager of the collection ${n === 1 ? 'it is' : 'they are'} in can do that. Nothing was moved.`;
}
